// ./src/components/features/CreateEventWizard/draftToEventInput.ts
//
// Maps the wizard draft to the API's EventInput. Local picker date strings are
// converted to UTC ISO for the server. Location fields are only sent when they
// apply to the chosen format. The event is created as a DRAFT — the owner
// publishes later.

// ⚠ The leaf package, not the `@/utils` barrel: the barrel drags in a PWA-only virtual module, and
// `check-event-setup` runs this file (via wizardSetup) under Node.
import { deadlineCeiling, deadlinePickerValue, zonedStringToUtc } from '@em/utils';
import { EventInput } from '@/utils/api';

import { EventWizardDraft, EVENT_FORMAT, EVENT_MODE, SCHEDULE_MODE, WizardMediaRef } from './wizardTypes';

/**
 * ROADMAP #137 — the latest the draft's deadline may be (a picker wall clock in the event's zone): its end, else its start;
 * an all-day event's last second. None on a REPEATING event (its dates close N days before each — #102) or before Basics
 * set a start. One source for the Guests step's cap, the shell's "the deadline follows the end", and the save.
 */
export const draftDeadlineCeiling = (draft: Pick<EventWizardDraft, 'scheduleMode' | 'startsAt' | 'endsAt' | 'isAllDay'>): string | null =>
  draft.scheduleMode !== SCHEDULE_MODE.RECURRING && draft.startsAt
    ? deadlineCeiling({ startsAt: draft.startsAt, endsAt: draft.endsAt, isAllDay: draft.isAllDay })
    : null;

// A cover pick is either a stored file (send its id) or an external URL
// (Unsplash / YouTube / Vimeo / direct link — send the url). Never both.
const coverFields = (
  media: WizardMediaRef | null,
  idKey: 'coverUploadedFileId' | 'coverVideoUploadedFileId',
  urlKey: 'coverUrl' | 'coverVideoUrl'
): Partial<EventInput> => {
  if (!media) {
    return { [idKey]: null, [urlKey]: null };
  }
  if (media.uploadedFileId) {
    return { [idKey]: media.uploadedFileId, [urlKey]: null };
  }
  return { [idKey]: null, [urlKey]: media.url || null };
};

export const draftToEventInput = (draft: EventWizardDraft): EventInput => {
  const isInPerson = draft.eventFormat === EVENT_FORMAT.IN_PERSON || draft.eventFormat === EVENT_FORMAT.HYBRID;
  const isOnline = draft.eventFormat === EVENT_FORMAT.ONLINE || draft.eventFormat === EVENT_FORMAT.HYBRID;
  const onlineUrl = draft.onlineMeetingUrl.trim();

  return {
    ...coverFields(draft.coverImage, 'coverUploadedFileId', 'coverUrl'),
    ...coverFields(draft.coverVideo, 'coverVideoUploadedFileId', 'coverVideoUrl'),

    title: draft.title.trim(),
    subtitle: draft.subtitle.trim() || null,
    eventCategoryId: draft.eventCategoryId,
    // ⚠ Only when the host was ASKED — absent lets the server attach the account's only organizer page.
    ...(draft.organizerProfileId !== undefined ? { organizerProfileId: draft.organizerProfileId } : {}),
    // #62 P6(d2) — the organizer page's blurb, only once the host has touched it (null / absent sends nothing).
    ...(draft.summary != null ? { summary: draft.summary.trim() || null } : {}),

    // ⚠ The pickers are wall clocks in the EVENT's time zone (the one chosen on this step), never the browser's.
    startsAt: zonedStringToUtc(draft.startsAt, draft.timezone || null),
    endsAt: zonedStringToUtc(draft.endsAt, draft.timezone || null),
    isAllDay: draft.isAllDay,
    timezone: draft.timezone,
    scheduleMode: draft.scheduleMode,

    eventFormat: draft.eventFormat,
    venueId: isInPerson ? draft.venueId : null,
    onlineMeetingUrl: isOnline && onlineUrl ? onlineUrl : null,

    eventMode: draft.eventMode,
    guestTracking: draft.guestTracking,
    guestsNeedApproval: draft.guestsNeedApproval,
    capacity: draft.capacity,
    rsvpGroupSize: draft.rsvpGroupSize,
    // #77 — its own field, not the plus-ones one: off = null = no limit. Only a ticketed event sells orders.
    maxTicketsPerOrder: draft.eventMode === EVENT_MODE.TICKETED ? draft.maxTicketsPerOrder ?? null : null,
    // #137 — a date AND time; a draft saved before #137 may hold a DAY, which means its last second (never its first).
    rsvpDeadline: zonedStringToUtc(deadlinePickerValue(draft.rsvpDeadline, draftDeadlineCeiling(draft)), draft.timezone || null),
    // A2e #102 — read only on a repeating event (the server decides by the schedule); ⚠ 0 is a real answer.
    rsvpDeadlineDaysBefore: draft.rsvpDeadlineDaysBefore ?? null,
    enableWaitlist: draft.enableWaitlist,

    // Born as a draft; the owner publishes from the event page.
    eventStatus: 'draft',
  };
};
